<template>
  <div class="page-content calendar scv clearfixing">
    <div class="page-title add-event">
      <router-link :to="{ name: 'Calendar' }">
        <span class="link uppercase-text">{{ $lang.messages.calendar }}</span>
      </router-link>
      <span> / </span>
      <span class="text">{{ $lang.messages.scheduleChildVisitation }}</span>
      <span class="box-name-info" @click="tooltip = true" :class="{ 'blue-box-info': tooltip }"></span>
      <f-p-tooltip
        v-if="tooltip"
        v-click-outside="hideTooltip"
        :hideTooltipInside="hideTooltip"
        :tooltipTitle="$lang.messages.tp_scheduleChildVisitationTitle"
        :tooltipContent="$lang.messages.tp_scheduleChildVisitationText">
      </f-p-tooltip>
    </div>
    <div class="page-header">
      <button class="generic-btn" @click="holidayScheduleModalOpen(selectedCoparent, selectedChildren)">
        {{ $lang.messages.holidaySchedule }}
        <img class="btn-icon" src='@/assets/images/payments/arrow_right_blue.png' style="opacity: 0"/>
      </button>

      <button class="generic-btn apply-pattern-btn" @click="applyPatternModalOpen(selectedCoparent, selectedChildren)">
        {{ $lang.messages.applyPattern }}
        <img class="btn-icon" src='@/assets/images/payments/arrow_right_blue.png' style="opacity: 0"/>
      </button>

      <div class="filter-select-btn export-calendar-menu">
        <button type="button" class="filter-select-btn" @click="exportOpened = !exportOpened">
          <span>{{ $lang.messages.exportCalendar }}</span>
          <img class="select-icon" src='@/assets/images/arrow_down.png' :class="{'select-icon-rotate': exportOpened}" />
        </button>
        <div class="action-menu" :class="!exportOpened ? 'select-menu-not-collapsed' : 'select-menu-collapsed'" >
          <div @click="print">
            {{ $lang.messages.print }}
          </div>
        </div>
      </div>
    </div>

    <!-- Apply Pattern Modal -->
    <apply-pattern-modal
      ref="applyPatternModalComponent"
      @reloadSelection="reloadSelection">
    </apply-pattern-modal>

    <holiday-schedule-modal
      ref="holidayScheduleModalComponent"
      :year="year"
      :holidays="holidays"
      :coparentArr="coparentHolidayArr"
      @reloadSelection="reloadSelection"
    >
    </holiday-schedule-modal>

    <section class="page-wrapper">
      <div
        v-if="coparentArr.length && !detectMobile()"
        class="coparent-selection"
      >

        <div class="calendar-header">
          <h4 class="uppercase-text calendar-subtitle">{{ $lang.messages.annualCalendar }}</h4>
          <div class="year">
            <div class="year-wrapper">
              <img src="@/assets/images/arrow_left_active.png" alt="" @click="changeYear('-')"/>
              <p>
                <strong>{{year}}</strong>
              </p>
              <img src="@/assets/images/arrow_right.png" alt="" @click="changeYear('+')"/>
            </div>
          </div>
        </div>

        <div class="staying-with">
          <div>
            <div></div>
            <span>
              {{ percentages[0].text }}
            </span>
          </div>
          <div >
            <div :style="{background: percentages[1].color}"></div>
            <span>
              {{ percentages[1].text }}
            </span>
          </div>
          <div>
            <img src="@/assets/images/warning_ico.png" alt="" />
            <span>{{ percentages[2].text }}</span>
          </div>
        </div>

        <h1 class="select-child">{{ $lang.messages.selectChild.toUpperCase() }}</h1>
        <div
          v-for="(cp, index) in coparentArr"
          :key="`compArr-${index}`"
          :style="{borderColor: cp.caseColor}"
          class="coparent-selector"
        >
          <div>
            <label class="checkbox-wrapper">
              <input
                type="checkbox"
                class="checkbox-input"
                @click="setCoparent(cp.coparentGuid)"
                :id="'checked' + cp.coparentGuid"
                v-model="cp.checked"
              >
              <span class="checkmark"></span>
            </label>
            <p @click="setCoparent(cp.coparentGuid)" :title="cp.coparentName">
              {{ MX_shrinkCoparentFullName(cp.coparentName) }}
            </p>
          </div>
          <div
            v-for="(child, index) in cp.children"
            :key="`children-${index}`"
            class="child-selector"
          >
            <label class="checkbox-wrapper">
              <input
                type="checkbox"
                class="checkbox-input"
                @click="setChild(child.childGuid, cp.coparentGuid)"
                :id="'checkedChild' + child.childGuid"
                v-model="child.checked"
              >
              <span class="checkmark"></span>
            </label>
            <p @click="setChild(child.childGuid, cp.coparentGuid)">{{child.name}}</p>
          </div>
        </div>
      </div>

      <div class="calendar-wrapper">
        <div class="mobile-views" v-if="detectMobile()">
          <div class="calendar-header">
            <h4 class="uppercase-text calendar-subtitle">{{ $lang.messages.annualCalendar }}</h4>
            <div class="year">
              <div class="year-wrapper">
                <img src="@/assets/images/arrow_left_active.png" alt="" @click="changeYear('-')"/>
                <p>
                  <strong>{{year}}</strong>
                </p>
                <img src="@/assets/images/arrow_right.png" alt="" @click="changeYear('+')"/>
              </div>
            </div>
          </div>

          <div class="mobile-views">
            <hr />
            <p>{{ $lang.messages.yearlyVisitDistribution }}</p>
            <div class="staying-with">
              <div>
                <div></div>
                <span>
                  {{ percentages[0].text }}
                </span>
              </div>
              <div >
                <div :style="{background: percentages[1].color}"></div>
                <span>
                  {{ percentages[1].text }}
                </span>
              </div>
              <div>
                <img src="@/assets/images/warning_ico.png" alt="" />
                <span>{{ percentages[2].text}}</span>
              </div>
            </div>
          </div>

          <p>{{ $lang.messages.selectChildren }}</p>
          <div class="input-wrapper input-field">
            <input
              type="text"
              class="input"
              v-model="childrenSearch"
              readonly="readonly"
              @input="onChange"
              @click="toggleChildren"
              autocomplete="off" />
            <span class="icon-wrapper" @click="toggleChildren">
              <img class="icon" src='@/assets/images/arrow_down.png' />
            </span>
          </div>
          <div
            v-if="coparentArr.length && showChildren"
            class="children-selector"
          >
            <div
              v-for="(cp, index) in coparentArr"
              :key="`cop-${index}`"
              :style="{borderColor: cp.caseColor}"
              class="coparent-selector"
            >
              <div>
                <label class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    class="checkbox-input"
                    @click="setCoparent(cp.coparentGuid)"
                    :id="'checked' + cp.coparentGuid"
                    v-model="cp.checked"
                  >
                  <span class="checkmark"></span>
                </label>
                <p @click="setCoparent(cp.coparentGuid)" >
                  {{ MX_shrinkCoparentFullName(cp.coparentName) }}
                </p>
              </div>

              <div
                v-for="(child, index) in cp.children"
                :key="`childSel-${index}`"
                class="child-selector"
              >
                <label class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    class="checkbox-input"
                    @click="setChild(child.childGuid, cp.coparentGuid)"
                    :id="'checkedChild' + child.childGuid"
                    v-model="child.checked"
                  >
                  <span class="checkmark"></span>
                </label>
                <p @click="setChild(child.childGuid, cp.coparentGuid)">{{child.name}}</p>
              </div>
            </div>
          </div>
          <button class="apply-pattern-btn-mobile" @click="holidayScheduleModalOpen(selectedCoparent, selectedChildren)">
            {{ $lang.messages.holidaySchedule }}
          </button>
          <button
            class="apply-pattern-btn-mobile"
            @click="applyPatternModalOpen(selectedCoparent, selectedChildren)"
          >
            {{ $lang.messages.applyPattern }}
          </button>
          <hr />
        </div>

        <div class="print-preview">
          <p>
            Yearly selected visitations for {{ this.selectedChildrenNames }} for {{ this.year }}
          </p>
        </div>

        <div class="clear-all-selected-wrapper">
          <div class="clear-all-selected" @click="confirmationModalOpen()">
            <p><b>✕</b>  {{ $lang.messages.clearAllSelections }}</p>
          </div>
          <!-- Confirmation Modal -->
          <confirmation-modal
            :modalTitle="$lang.messages.clearAllSelections"
            :text="$lang.messages.sureClearSelection + '?'"
            :buttonText="$lang.messages.clearAllSelections"
            ref="confirmationModal"
            @emitAction="clearAllSelected">
          </confirmation-modal>
        </div>

        <div
          v-for="(j, index) in computedList"
          :key="`compList-${index}`"
          class="month-list"
        >
          <div class="arrow-left" id="arrowLeft">
            <img src="@/assets/images/arrow_left_active.png" alt="" v-show="noOfMonths === 1"/>
          </div>
          <div
            :class="new Date(year, noOfMonths > 1 ? index + 1 : currentSelectedMonth + 1, 0) < new Date() ? 'past-dates' : ''"
            class="month-selector"
          >
            <b>{{months[noOfMonths > 1 ? (new Date(year, index + 1, 0).getMonth()) : (new Date(year, currentSelectedMonth + 1, 0).getMonth())]}}</b>
          </div>
          <div class="arrow-right" id="arrowRight">
            <img src="@/assets/images/arrow_right.png" alt="" v-show="noOfMonths === 1"/>
          </div>
          <div
            v-for="(w, index) in weekDays"
            :key="`weekDays-${index}`"
            :class="new Date(year, noOfMonths > 1 ? index + 1 : currentSelectedMonth + 1, 0) < new Date() ? 'past-dates weeks' : 'weeks'">
            <p>
              {{w}}
            </p>
          </div>
          <div
            v-for="(i, indexJ) in j"
            :key="`utcDate-${indexJ}`"
            :ref="i"
            :id="returnUTCDate(noOfMonths > 1 ? index : currentSelectedMonth, i + 1)"
            :class="i === -1 ?
            'ghost-date' : (new Date(year, noOfMonths > 1 ? index: currentSelectedMonth, i + 2)) < new Date() ? 'selectables past-dates' : 'selectables'"
          >
            <div
                :class="{'holiday' : isDateHoliday(i+1, months[noOfMonths > 1 ? (new Date(year, index + 1, 0).getMonth()) : (new Date(year, currentSelectedMonth + 1, 0).getMonth())], year)}"
                :title="holidayTitle(i+1, months[noOfMonths > 1 ? (new Date(year, index + 1, 0).getMonth()) : (new Date(year, currentSelectedMonth + 1, 0).getMonth())], year)"
            >
              {{i + 1}}
            </div>
            <img
              v-show="calcMixedTimes(noOfMonths > 1 ? index : currentSelectedMonth, i + 1, rerender)"
              src="@/assets/images/warning_ico.png"
              alt=""
              class="mixed-parents"
              :title="holidayTitle(i+1, months[noOfMonths > 1 ? (new Date(year, index + 1, 0).getMonth()) : (new Date(year, currentSelectedMonth + 1, 0).getMonth())], year)"
            />
            <span
              v-show="calcMixedTimes(noOfMonths > 1 ? index : currentSelectedMonth, i + 1, rerender)"
              class="span-mixed"
            >
              <div>{{ calcMixedChildren(noOfMonths > 1 ? index : currentSelectedMonth, i + 1, rerender).stayingWithLogged }}</div>
              <div>{{ calcMixedChildren(noOfMonths > 1 ? index : currentSelectedMonth, i + 1, rerender).stayingWithOther }}</div>
            </span>
          </div>
        </div>
        <div class="arrow-left-clickable" @click="changeMonth('-')" v-if="noOfMonths === 1">
        </div>
        <div class="arrow-right-clickable" @click="changeMonth('+')" v-if="noOfMonths === 1">
        </div>
      </div>

    </section>
  </div>
</template>

<script>
    import Selection from '@simonwep/selection-js';
    import {mapActions, mapGetters} from "vuex";
    import {ADD_NEW_VISITATION, DELETE_VISITATION, GET_HOLIDAYS, GET_VISITATIONS_V2} from "@/store/action.types";
    import ApplyPatternModal from "@/components/appLayout/views/calendar/ApplyPatternModal";
    import ConfirmationModal from "@/components/common/modals/ConfirmationModal";
    import HolidayScheduleModal from "@/components/appLayout/views/calendar/HolidayScheduleModal";
    import vClickOutside from 'v-click-outside'
    import {dateFormat, requestDateFormatUTC} from '@/mixins/dateFormat';
    import { shrinkCoparentFullName } from "@/mixins/shrinkCoparentFullName";
    import STATUS_CODES from "@/common/statusCodes";

    export default {
    name: "ScheduleChildVisitation",
    components: {
      HolidayScheduleModal,
      ConfirmationModal,
      ApplyPatternModal,
      'f-p-tooltip': () => import('@/components/common/FPTooltip'),
    },
    directives: {
      clickOutside: vClickOutside.directive
    },
    mixins: [
      dateFormat,
      requestDateFormatUTC,
      shrinkCoparentFullName
    ],
    data() {
      return {
        destroySelection: null,
        rerender: 0,
        selectedCoparent: Object,
        initArr: null,
        currentParentGuid: '',
        selectedChildren: [],
        coparents: [],
        year: new Date().getFullYear(),
        months: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
        weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        noOfMonths: 0,
        currentSelectedMonth: new Date().getMonth(),
        showChildren: false,
        childrenSearch: '',
        holidays: [],
        tooltip: false,
        firstDayOfWeek: 1,
        coparentHolidayArr: [],
        mixed: [],
        percentages: [{text: '', color: ''},{text: '', color: ''},{text: '', color: ''},],
        exportOpened: false,
      }
    },
    computed: {
      ...mapGetters({
        coparentGuid: 'coparentGuid',
        coparentsStore: 'coparents',
      }),
      selectedChildrenNames() {
        let names = '';

        this.selectedChildren.forEach(ch => {
            this.cases.forEach(ca => {
                for (let i of ca.children) {
                  if (i.childGuid === ch) {
                      names += i.name + ', ';
                  }
                }
            })
        });

        names = names.slice(0, -2);

        return names;
      },
      coparentName() {
        let name = '';

        for (let i of this.coparentsStore) {
          if (i.guid === this.coparentGuid) {
            name = i.name;
            break;
          }
        }

        return name;
      },
      cases() {
        return this.$store.getters.cases
      },
      //This will create our calendar matrix
      // noOfMonths here is used as a mobile/low-power device selector to not add ~365 divs, but rather ~31 divs.
      // currentSelectedMonth is only used to load the correct month if on mobile otherwise it behaves the same way the normal for loop does.
      computedList() {
        let x = [];
        for (let j = 0; j < this.noOfMonths; j++) {
          let actualI = 0;
          x.push([]);
          if(this.noOfMonths > 1) {
            this.currentSelectedMonth = j;
          }
          const offset = (this.calcDate(this.currentSelectedMonth, 1).getDay() - this.firstDayOfWeek) < 0 ? 7 + this.calcDate(this.currentSelectedMonth, 1).getDay() - this.firstDayOfWeek : this.calcDate(this.currentSelectedMonth, 1).getDay() - this.firstDayOfWeek;
          for (let i = 0; i < (this.calcDate(this.currentSelectedMonth + 1, 0).getDate() + offset); i++) {
            if(i < offset) {
              x[j].push(-1)
            }
            else {
              x[j].push(actualI);
              actualI++;
            }
          }
        }
        return x;
      },
      coparentArr() {
        return this.coparents.filter(elem => {
          return elem.children.length
        })
      }
    },
    watch: {
      selectedChildren: function() {
        this.childrenSearch = '';
        for (let i of this.coparents) {
          i.children.forEach(child => {
           if (child.checked) {
             if (this.childrenSearch === '') {
               this.childrenSearch = child.name;
             }
             else {
               this.childrenSearch = this.childrenSearch + ', ' + child.name
             }
           }
          });
        }
      },
      cases: function() {
        this.loadCoparents();
      }
    },
    created() {
      // http://detectmobilebrowsers.com/
      // ^ Mobile selector added from here. ^
      if (this.detectMobile()) {
        this.noOfMonths = 1;
      }
      else {
        this.noOfMonths = 12;
      }
    },
    mounted() {
      this.weekDays = this.sortByDayOfWeek(this.weekDays, this.firstDayOfWeek);
      if(this.cases.length > 0) {
        this.loadCoparents();
      }
      this.loadHolidays();
    },
    destroyed() {
      this.destroySelection.destroy();
    },
    methods: {
      ...mapActions({
        getVisitations: GET_VISITATIONS_V2,
        postNewVisitation: ADD_NEW_VISITATION,
        deleteVisitation: DELETE_VISITATION,
        getHolidaysAction: GET_HOLIDAYS,
      }),
      applyPatternModalOpen(selectedCoparent, selectedChildren) {
        this.$refs.applyPatternModalComponent.show(selectedCoparent, selectedChildren);
      },
      holidayScheduleModalOpen(selectedCoparent, selectedChildren) {
        this.holidaySearch(this.holidays);
        this.$refs.holidayScheduleModalComponent.show(selectedCoparent, selectedChildren);
      },
      toggleChildren() {
        this.showChildren = !this.showChildren;
      },
      print() {
          this.exportOpened = !this.exportOpened;
          setTimeout(function(){
              window.print();
          }, 300);
      },
      onChange() {
      },
      sortByDayOfWeek(arr, dayOfWeek) {
        return arr.slice(dayOfWeek).concat(arr.slice(0, dayOfWeek));
      },
      returnUTCDate(month, day, year = this.year ) {
        return Date.UTC(year, month, day);
      },
      holidaySearch(holidays) {
        this.coparentHolidayArr = [];
        let selected = document.getElementsByClassName('selected');
        for (let holiday of holidays) {
          let forHol = new Date(holiday.date);
          forHol = Date.UTC(forHol.getUTCFullYear(), forHol.getUTCMonth(), forHol.getUTCDate());
          for (let s of selected) {
            if (forHol === parseInt(s.id)) {
              if (s.classList.contains(this.selectedCoparent.coparentGuid)) {
                this.coparentHolidayArr.push({date: holiday.date, coparent: this.selectedCoparent.coparentName})
              }
              else {
                this.coparentHolidayArr.push({date: holiday.date, coparent: this.coparentName})
              }
            }
          }
        }
      },
      loadCoparents() {
        for (let i = 0; i < this.cases.length; i++) {
          this.cases[i].checked = false;
          if (this.cases[i].children.length > 0) {
            for (let j = 0; j < this.cases[i].children.length; j++) {
              this.cases[i].children[j].checked = false;
            }
          }
        }
        this.coparents = this.cases;
        for (let c of this.coparents) {
          if(c.children.length > 0) {
            c.checked = true;
            this.selectedCoparent = c;
            break;
          }
        }
        for (let j = 0; j < this.selectedCoparent.children.length; j++) {
          this.coparents[this.coparents.indexOf(this.selectedCoparent)].children[j].checked = true;
          this.selectedChildren.push(this.coparents[this.coparents.indexOf(this.selectedCoparent)].children[j].childGuid);
        }
        this.loadVisitations(this.selectedCoparent.coparentGuid, this.selectedCoparent.caseColor);
      },
      mapPercentagesMixins(visitations, percentages) {
          this.mixed = [];
          visitations.forEach(visitation => {
              let date = new Date(visitation.date);
              let offset = date.getTimezoneOffset() > 0 ? 1 : 0;
              let dateUTC = this.returnUTCDate(date.getMonth(), date.getDate() + offset);
              if (visitation.mixedMessages) {
                  this.mixed.push({
                      mixed: true,
                      date: dateUTC,
                      mixedMessages: visitation.mixedMessages,
                  });
              }
          });

          this.percentages = percentages;
      },

      /*
       *  @param {guid} guid Co-parent guid being loaded from the backend
       **/
      loadVisitations(guid) {
        let endDate = this.year + '-12-31';
        let startDate= this.year + '-01-01';
        let month = this.currentSelectedMonth < 9 ? '0' + (this.currentSelectedMonth + 1) : (this.currentSelectedMonth + 1);
        if (this.noOfMonths === 1) {
            endDate = this.year + '-' + month + '-31';
            startDate = this.year + '-' + month + '-01';
        }
        this.initArr = document.getElementsByClassName('selected');
        if (this.initArr) {
          this.clearAllSelected(false);
        }
        this.getVisitations({
            coparentGuid: guid,
            childrenGuids: this.selectedChildren,
            startDate: startDate,
            endDate: endDate
        })
        .then(response => {
          if (response.data.data.visitations.length > 0) {

              const visitations = response.data.data.visitations;
              visitations.forEach(visitation => {
                 let date = new Date(visitation.date);
                 let offset = date.getTimezoneOffset() > 0 ? 1 : 0;
                 let dateUTC = this.returnUTCDate(date.getMonth(), date.getDate() + offset);
                 let x = document.getElementById(dateUTC.toString());
                 if (visitation.color) {
                     x.classList.add(this.selectedCoparent.coparentGuid);
                     x.firstChild.style.background = visitation.color;
                 }
                 x.classList.add('selected');
              });

              this.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
          }
          if (this.destroySelection) {
              this.destroySelection.destroy();
          }
          this.destroySelection =
              this.loadSelection(this.coparents, this.selectedCoparent, this.selectedChildren, this.postNewVisitation, this.deleteVisitation, this.getVisitations, this.percentages, this.year, this.currentSelectedMonth, this.noOfMonths);
        })
        .catch(error => {
          if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      clearAllSelected(deleteArr = true) {
        const dates = document.getElementsByClassName('selected');
        const selectables = document.getElementsByClassName('selectables');
        if (deleteArr) {
          let delArr = [];
          for (let date of dates) {
            if (date.id) {
              const d = new Date(parseInt(date.id) + (new Date(parseInt(date.id)).getTimezoneOffset() * 60000));
              const year = d.getFullYear();
              const month = d.getMonth() < 9 ? '0' + (d.getMonth() + 1) : (d.getMonth() + 1);
              const day = d.getDate() < 10 ? '0' + d.getDate() : d.getDate();
              delArr.push(year + '-' + month + '-' + day);
            }
          }
          this.deleteVisitation({
            childrenGuids: this.selectedChildren,
            coparentGuid: this.selectedCoparent.coparentGuid,
            datesForDeletion: delArr
          })
          .then(() => {
            for (let s of selectables) {
              s.classList.remove('selected');
              s.classList.remove('first-touch');
              s.classList.remove('second-touch');
              s.classList.remove(this.selectedCoparent.coparentGuid);
              s.firstChild.style.background = '';
            }
            let endDate = this.year + '-12-31';
            let startDate= this.year + '-01-01';
            let month = this.currentSelectedMonth < 9 ? '0' + (this.currentSelectedMonth + 1) : (this.currentSelectedMonth + 1);
            if (this.noOfMonths === 1) {
                endDate = this.year + '-' + month + '-31';
                startDate = this.year + '-' + month + '-01';
            }
            this.getVisitations({
                  coparentGuid: this.selectedCoparent.coparentGuid,
                  childrenGuids: this.selectedChildren,
                  startDate: startDate,
                  endDate: endDate
              }).then(response => {
                this.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
              }).catch(error => {
                  if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                      this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
                  }
              });
            this.mixed = [];
            this.initArr = null;
            this.rerender++;
          })
          .catch(error => {
            if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          });
        }
        else {
          for (let s of selectables) {
            s.classList.remove('selected');
            s.classList.remove('first-touch');
            s.classList.remove('second-touch');
            s.classList.remove(this.selectedCoparent.coparentGuid);
            s.firstChild.style.background = '';
          }
          let endDate = this.year + '-12-31';
          let startDate= this.year + '-01-01';
          let month = this.currentSelectedMonth < 9 ? '0' + (this.currentSelectedMonth + 1) : (this.currentSelectedMonth + 1);
          if (this.noOfMonths === 1) {
              endDate = this.year + '-' + month + '-31';
              startDate = this.year + '-' + month + '-01';
          }
          this.getVisitations({
              coparentGuid: this.selectedCoparent.coparentGuid,
              childrenGuids: this.selectedChildren,
              startDate: startDate,
              endDate: endDate
          }).then(response => {
              this.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
          }).catch(error => {
              if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                  this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
              }
          });
          this.mixed = [];
          this.initArr = null;
          this.rerender++;
        }
      },
      changeYear(direction) {
        let x = document.getElementsByClassName('selectables');
        if (direction === '-') {
          this.year--;
        } else {
          this.year++;
        }
        for(let i of x) {
           i.classList.remove('selected');
           i.firstChild.style.background = '';
           i.classList.remove(this.selectedCoparent.coparentGuid)
        }
        this.loadVisitations(this.selectedCoparent.coparentGuid, this.selectedCoparent.caseColor, this.selectedChildren);
        this.loadHolidays();
      },
      changeMonth(direction) {
        let x = document.getElementsByClassName('selectables');
        if (direction === '-') {
            if(this.currentSelectedMonth === 0) {
                this.currentSelectedMonth = 11;
                this.year--;
            }
            else {
                this.currentSelectedMonth--;
            }
        } else {
            if(this.currentSelectedMonth === 11) {
                this.currentSelectedMonth = 0;
                this.year++;
            }
            else {
                this.currentSelectedMonth++;
            }
        }
        for(let i of x) {
          i.classList.remove('selected');
          i.firstChild.style.background = '';
          i.classList.remove(this.selectedCoparent.coparentGuid)
        }
        this.loadVisitations(this.selectedCoparent.coparentGuid, this.selectedCoparent.caseColor, this.selectedChildren);
      },
      calcDate(month, day) {
        return new Date(this.year, month, day)
      },
      calcMixedTimes(index, i) {
        let bool = false;
        this.mixed.forEach(mix => {
            if (mix.date === this.returnUTCDate(index, i)) {
                bool = true;
            }
        });
        return bool;
      },
      calcMixedChildren(index, i) {
          let rtStr = {
              stayingWithLogged: '',
              stayingWithOther: '',
          };
          this.mixed.forEach(mix => {
              if (mix.date === this.returnUTCDate(index, i)) {
                  rtStr = mix.mixedMessages;
              }
          });
          return rtStr;
      },
      reloadSelection() {
        this.loadVisitations(this.selectedCoparent.coparentGuid, this.selectedCoparent.caseColor);
      },
      setCoparent(guid) {
        if (this.selectedCoparent.coparentGuid === guid) {
          document.getElementById('checked' + guid).checked = true;
        }
        let x = document.getElementsByClassName('selectables');
        this.selectedChildren = [];
        for (let cp of this.coparents) {
          if (cp.coparentGuid === guid) {
            this.selectedCoparent = cp;
            this.loadVisitations(guid, this.selectedCoparent.caseColor);
            cp.checked = true;
            for (let child of cp.children) {
              this.selectedChildren.push(child.childGuid);
              child.checked = true;
            }
          }
          else {
            for (let i of x) {
              i.firstChild.style.background = '';
              i.classList.remove(cp.coparentGuid);
              i.classList.remove('selected');
              i.classList.remove('first-touch');
              i.classList.remove('second-touch');
            }
            cp.checked = false;
            for(let child of cp.children) {
              child.checked = false;
            }
          }
        }
      },
      setChild(guid, coparentGuid) {
        if (this.selectedCoparent.coparentGuid === coparentGuid && this.selectedChildren.length === this.selectedCoparent.children.length) {
          document.getElementById('checkedChild' + guid).checked = true;
        }
        else if (this.selectedCoparent.coparentGuid === coparentGuid && this.selectedChildren.length === 1) {
          document.getElementById('checkedChild' + guid).checked = true;
        }
        this.mixed = [];
        let x = document.getElementsByClassName('selectables');
        for (let cp = 0; cp < this.coparents.length; cp++) {
          if (this.coparents[cp].coparentGuid === coparentGuid) {
            if (this.selectedCoparent.coparentGuid !== coparentGuid) {
              this.selectedCoparent = this.coparents[cp];
              this.selectedChildren = [];
            }
            if (this.selectedChildren.indexOf(guid) !== -1 && this.selectedChildren.length !== 1) {
              this.selectedChildren.splice(this.selectedChildren.indexOf(guid), 1);
            }
            else if (this.selectedChildren.indexOf(guid) === -1) {
              this.selectedChildren.push(guid);
            }
            this.coparents[cp].checked = true;
            for (let child of this.selectedCoparent.children) {
              if (this.selectedChildren.indexOf(child.childGuid) !== -1) {
                this.loadVisitations(this.selectedCoparent.coparentGuid, this.selectedCoparent.caseColor, this.selectedChildren);
                child.checked = true;
              }
              else {
                for (let i of x) {
                  i.firstChild.style.background = '';
                  i.classList.remove(this.coparents[cp].coparentGuid);
                  i.classList.remove('selected');
                  i.classList.remove('first-touch');
                  i.classList.remove('second-touch');
                }
                if(this.selectedChildren.indexOf(child.childGuid) === -1) {
                  child.checked = false;
                }
              }
            }
          }
          else {
            this.coparents[cp].checked = false;
            for (let i of x) {
              i.firstChild.style.background = '';
              i.classList.remove(this.coparents[cp].coparentGuid);
              i.classList.remove('selected');
              i.classList.remove('first-touch');
              i.classList.remove('second-touch');
            }
            for (let child of this.coparents[cp].children) {
              child.checked = false;
            }
          }
        }
      },
      //TODO: Refactor all the below params to be self.something
      /**
       * Oh god here lie monsters!
       * This is our implementation of @simonwep/selection-js NPM.
       * This function is used for one thing and one thing alone, SELECTING SHIT!, it doesn't work well with Vue data.
       * And needs to be destroyed and rebuilt from scratch every time it's being called.
       *
       * @param coparents            -- literally just this.coparents
       * @param uiSelected           -- literally just this.selectedCoparent
       * @param selectedChildren     -- literally just this.selectedChildren
       * @param postChildren         -- literally just this.postChildren
       * @param deleteChildren       -- literally just this.deleteChildren
       * @param getVisitations       -- literally just this.getVisitations
       * @param percentages          -- literally just this.percentages
       * @param year                 -- literally just this.year
       * @param currentSelectedMonth -- literally just this.currentSelectedMonth
       * @param noOfMonths           -- literally just this.noOfMonths
       * Told ya it doesn't work well with Vue data!
       * @returns {Selection} so that we can destroy() it when the time is right.
       */
      loadSelection(coparents, uiSelected, selectedChildren, postChildren, deleteChildren, getVisitations, percentages, year, currentSelectedMonth, noOfMonths) {
        let shiftSelect = null;
        let endDate = year + '-12-31';
        let startDate= year + '-01-01';
        let month = currentSelectedMonth < 9 ? '0' + (currentSelectedMonth + 1) : (currentSelectedMonth + 1);
        if (noOfMonths === 1) {
            endDate = year + '-' + month + '-31';
            startDate = year + '-' + month + '-01';
        }
        let self = this;
        if(coparents.length > 0) {

          //We return just so we can destroy() later on #vengance
          return Selection.create({
            //What class the selected HTML element will have
            class: 'selection',
            //What can be selected on the HTML page
            selectables: ['.month-list > .selectables'],
            //Where you can drag your mouse around and watch the magic happen
            boundaries: ['.month-list'],

            //onClickReleased event
            onSelect(evt) {
              const selected = evt.target.classList.contains('selected');

              if (evt.originalEvent.shiftKey) {
                if (shiftSelect) {
                  let selArr = {
                    parent: [],
                    coparent: [],
                    delete: []
                  };
                  let date = (d) => {
                    const date = new Date(parseInt(d) + new Date(parseInt(d)).getTimezoneOffset() * 60000);
                    const year = date.getFullYear();
                    const month = date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1);
                    const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
                    return year + '-' + month + '-' + day;
                  };
                  shiftSelect = (parseInt(shiftSelect) + 86400000).toString();
                  while (parseInt(shiftSelect) < parseInt(evt.target.id)) {
                    if (!document.getElementById(shiftSelect).classList.contains('selected')) {
                      document.getElementById(shiftSelect).classList.add('selected');
                      selArr.parent.push(date(shiftSelect));
                    } else if (!document.getElementById(shiftSelect).classList.contains(uiSelected.coparentGuid)) {
                      document.getElementById(shiftSelect).classList.add(uiSelected.coparentGuid);
                      document.getElementById(shiftSelect).firstChild.style.background = uiSelected.caseColor;
                      selArr.coparent.push(date(shiftSelect));
                    } else {
                      document.getElementById(shiftSelect).classList.remove('selected');
                      document.getElementById(shiftSelect).classList.remove(uiSelected.coparentGuid);
                      document.getElementById(shiftSelect).firstChild.style.background = '';
                      selArr.delete.push(date(shiftSelect));
                    }
                    shiftSelect = (parseInt(shiftSelect) + 86400000).toString();
                  }

                  if (selArr.parent.length > 0 || selArr.coparent.length > 0) {
                    postChildren({
                      childrenGuids: selectedChildren,
                      coparentGuid: uiSelected.coparentGuid,
                      coparentDates: selArr.coparent,
                      loggedCoparentDates: selArr.parent
                    })
                      .then(() => {
                          getVisitations({
                              coparentGuid: uiSelected.coparentGuid,
                              childrenGuids: selectedChildren,
                              startDate: startDate,
                              endDate: endDate
                          }).then(response => {
                              self.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
                          }).catch(error => {
                              if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                                  self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                              }
                          });
                      })
                      .catch(error => {
                        if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                            self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                        }
                      });
                  }
                  if (selArr.delete.length > 0) {
                    deleteChildren({
                      childrenGuids: selectedChildren,
                      coparentGuid: uiSelected.coparentGuid,
                      datesForDeletion: selArr.delete
                    })
                      .then(() => {
                      })
                      .catch(error => {
                        if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                            self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                        }
                      });
                  }
                }
                shiftSelect = null;
              }
              else {
                shiftSelect = evt.target.id;
              }
              if (!selected) {
                evt.target.classList.add('selected');
                evt.target.classList.add('first-touch');
                self.rerender++;
              } else if (!evt.target.classList.contains(uiSelected.coparentGuid)) {
                evt.target.classList.add(uiSelected.coparentGuid);
                evt.target.firstChild.style.background = uiSelected.caseColor;
                evt.target.classList.remove('first-touch');
                self.rerender++;
              } else {
                evt.target.classList.remove('selected');
                evt.target.firstChild.style.background = '';
                evt.target.classList.remove(uiSelected.coparentGuid);
              }

              const date = new Date(parseInt(evt.target.id) + new Date(parseInt(evt.target.id)).getTimezoneOffset() * 60000);
              const year = date.getFullYear();
              const month = date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1);
              const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();

              if (evt.target.classList.contains('selected')) {
                if (!evt.target.classList.contains(uiSelected.coparentGuid)) {
                  postChildren({
                    childrenGuids: selectedChildren,
                    coparentGuid: uiSelected.coparentGuid,
                    coparentDates: [],
                    loggedCoparentDates: [year + '-' + month + '-' + day]
                  })
                    .then(() => {
                        getVisitations({
                            coparentGuid: uiSelected.coparentGuid,
                            childrenGuids: selectedChildren,
                            startDate: startDate,
                            endDate: endDate
                        }).then(response => {
                            self.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
                        }).catch(error => {
                            if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                                self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                            }
                        });
                    })
                    .catch(error => {
                      if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                          self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                      }
                    });
                } else if (
                    !evt.target.classList.contains('first-touch') &&
                    evt.target.classList.contains(uiSelected.coparentGuid) &&
                    !evt.target.classList.contains('second-touch')
                ) {
                  deleteChildren({
                    childrenGuids: selectedChildren,
                    coparentGuid: uiSelected.coparentGuid,
                    datesForDeletion: [year + '-' + month + '-' + day]
                  })
                    .then(() => {
                      postChildren({
                        childrenGuids: selectedChildren,
                        coparentGuid: uiSelected.coparentGuid,
                        coparentDates: [year + '-' + month + '-' + day],
                        loggedCoparentDates: []
                      })
                      .then(() => {
                        evt.target.classList.add('second-touch');
                          getVisitations({
                              coparentGuid: uiSelected.coparentGuid,
                              childrenGuids: selectedChildren,
                              startDate: startDate,
                              endDate: endDate
                          }).then(response => {
                              self.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
                          }).catch(error => {
                              if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                                  self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                              }
                          });
                      })
                  })
                  .catch(error => {
                    if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                      self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                    }
                  });
                }
              } else {
                deleteChildren({
                  childrenGuids: selectedChildren,
                  coparentGuid: uiSelected.coparentGuid,
                  datesForDeletion: [year + '-' + month + '-' + day]
                })
                .then(() => {
                  evt.target.classList.remove('second-touch');
                })
                .catch(error => {
                  if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                      self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                  }
                });
              }
            },

            //onDragStart event
            onStart({selectedElements, originalEvent}) {

            },

            //onDragMove event
            onMove({selectedElements}) {
              let firstTouch = false;
              for (let i of selectedElements) {
                if (i.classList.contains('first-touch')) {
                  firstTouch = true;
                  break;
                }
              }
              selectedElements.forEach(s => {
                if (!s.classList.contains('dragged')) {
                  if (s.classList.contains(uiSelected.coparentGuid)) {
                    s.classList.remove(uiSelected.coparentGuid);
                    s.firstChild.style.background = '';
                  } else if (s.classList.contains('selected') || firstTouch) {
                    if(!selectedElements[0].classList.contains(uiSelected.coparentGuid)) {
                      selectedElements[0].classList.add(uiSelected.coparentGuid);
                      selectedElements[0].firstChild.style.background = uiSelected.caseColor;
                    }
                    s.classList.add('selected');
                    s.classList.add(uiSelected.coparentGuid);
                    s.firstChild.style.background = uiSelected.caseColor;
                  } else {
                    s.classList.add('selected');
                  }
                }
                s.classList.add('dragged');
              });
            },
            //onDragStopAndMouseReleased event
            onStop({selectedElements}) {
              let selArr = {
                parent: [],
                coparent: [],
                delete: []
              };
              selectedElements.forEach(s => {
                s.classList.remove('dragged');
                const date = (date) => {
                  date = new Date(parseInt(date) + new Date(parseInt(date)).getTimezoneOffset() * 60000);
                  const year = date.getFullYear();
                  const month = date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1);
                  const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
                  return year + '-' + month + '-' + day
                };
                if(s.classList.contains(uiSelected.coparentGuid)) {
                  selArr.coparent.push(date(s.id));
                }
                else {
                  selArr.parent.push(date(s.id));
                }
              });

              postChildren({
                childrenGuids: selectedChildren,
                coparentGuid: uiSelected.coparentGuid,
                coparentDates: selArr.coparent,
                loggedCoparentDates: selArr.parent
              })
                .then(() => {
                    getVisitations({
                        coparentGuid: uiSelected.coparentGuid,
                        childrenGuids: selectedChildren,
                        startDate: startDate,
                        endDate: endDate
                    }).then(response => {
                        self.mapPercentagesMixins(response.data.data.visitations, response.data.data.percentages);
                    }).catch(error => {
                        if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                            self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                        }
                    });
                })
                .catch(error => {
                  if (error.response && (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402)) {
                      self.$snotify.error(self.$lang.messages.sn_genericError, 'ERROR');
                  }
                });

              self.rerender++;
            }
          });
        }
      },
      confirmationModalOpen() {
        this.$refs.confirmationModal.show();
      },
      detectMobile() {
        return (function(a) {
          return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4));
        })(navigator.userAgent || navigator.vendor || window.opera);
      },
      hideTooltip(e) {
        e.stopPropagation();
        this.tooltip = false;
      },
      loadHolidays() {
        this.getHolidaysAction({
          year: this.year,
          countryCode: localStorage.getItem('countryCode')
        })
        .then(response => {
          this.holidays = response.data.data;
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        })
      },
      isDateHoliday(paramDate, paramMonth, paramYear) {
        let currentDate = this.requestDateFormatUTC(new Date(paramYear, this.months.indexOf(paramMonth), paramDate));
        let found = false;
        this.holidays.forEach(holiday => {
          if(holiday.date === currentDate) {
            found = true
          }
          else if (holiday.durationDays > 0) {
            for (let i = 0; i <= holiday.durationDays; i++) {
              let foundDate = new Date(currentDate);
              if (this.requestDateFormatUTC(foundDate.setDate(foundDate.getDate() - i)) === holiday.date) {
                found = true;
                break;
              }
            }
          }
        });
        return found
      },
      holidayTitle(paramDate, paramMonth, paramYear) {
        let currentDate = this.requestDateFormatUTC(new Date(paramYear, this.months.indexOf(paramMonth), paramDate));
        let name = '';
        this.holidays.forEach(holiday => {
          if(holiday.date === currentDate) {
            name = holiday.name
          }
          else if (holiday.durationDays > 0) {
              for (let i = 0; i <= holiday.durationDays; i++) {
                  let foundDate = new Date(currentDate);
                  if (this.requestDateFormatUTC(foundDate.setDate(foundDate.getDate() - i)) === holiday.date) {
                      name = holiday.name;
                      break;
                  }
              }
          }
        });
        return name
      }
    }
  }
</script>
